import { t } from "@lingui/macro";
import {
  CreateWorkspaceSmtpSettingsDocument,
  type CreateWorkspaceSmtpSettingsMutation,
  type CreateWorkspaceSmtpSettingsMutationVariables,
  SendTestEmailDocument,
  type SendTestEmailMutation,
  type SendTestEmailMutationVariables,
  TestSmtpConnectionDocument,
  type TestSmtpConnectionMutation,
  type TestSmtpConnectionMutationVariables,
  UpdateWorkspaceSmtpSettingsDocument,
  type UpdateWorkspaceSmtpSettingsMutation,
  type UpdateWorkspaceSmtpSettingsMutationVariables,
  type WorkspaceSettingsQuery,
} from "@src/__generated__/urql-graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { SmtpSettingsState } from "@src/stores/forms/SettingsModalState/SmtpSettingsState";
import { action, computed, makeObservable, observable } from "mobx";

export class SmtpSettingsStore implements BaseStore {
  @observable smtpSettingsId: string | null = null;

  @observable.ref
  isGoogleConnected = false;

  @observable.ref
  isMicrosoftConnected = false;

  private createMutator = new MutationHelper<
    CreateWorkspaceSmtpSettingsMutation,
    CreateWorkspaceSmtpSettingsMutationVariables
  >(CreateWorkspaceSmtpSettingsDocument);

  private updateMutator = new MutationHelper<
    UpdateWorkspaceSmtpSettingsMutation,
    UpdateWorkspaceSmtpSettingsMutationVariables
  >(UpdateWorkspaceSmtpSettingsDocument);

  private testMutator = new MutationHelper<
    TestSmtpConnectionMutation,
    TestSmtpConnectionMutationVariables
  >(TestSmtpConnectionDocument);

  private sendTestEmailMutator = new MutationHelper<
    SendTestEmailMutation,
    SendTestEmailMutationVariables
  >(SendTestEmailDocument);

  constructor(
    public appStore: AppStore,
    public form: SmtpSettingsState,
  ) {
    makeObservable(this);
  }

  @computed
  get isLoadingSmtpTest(): boolean {
    return this.testMutator.isLoading.value;
  }

  @computed
  get isLoadingSendTestEmail(): boolean {
    return this.sendTestEmailMutator.isLoading.value;
  }

  @action.bound
  init(data: WorkspaceSettingsQuery) {
    const settings = data.workspaceSmtpSettings;
    this.smtpSettingsId = settings?.id ?? null;
    this.isGoogleConnected = settings?.is_google_connected ?? false;
    this.isMicrosoftConnected = settings?.is_microsoft_connected ?? false;
  }

  @action.bound
  async saveSmtpSettings(): Promise<boolean> {
    if (!this.appStore.workspaceStore.settings?.showSmtpSettings) return true;
    if (!this.form.isDirty) return true;

    if (this.smtpSettingsId) {
      const input = await this.form.getUpdateMutationInput();
      if (!input) return false;

      const [data, error] = await this.updateMutator.mutate({
        id: this.smtpSettingsId,
        input,
      });

      if (error) return false;

      this.smtpSettingsId = data.updateWorkspaceSmtpSettings.id;
    } else {
      const input = await this.form.getCreateMutationInput();
      if (!input) return false;

      const [data, error] = await this.createMutator.mutate({
        input,
      });

      if (error) return false;

      this.smtpSettingsId = data.createWorkspaceSmtpSettings.id;
    }

    return true;
  }

  @action.bound
  async testSmtpConnection() {
    const input = await this.form.getTestConnectionInput();
    if (!input) return;

    const [data, error] = await this.testMutator.mutate({
      input,
    });

    if (error) return;

    this.appStore.UIStore.toast({
      title: data.testSmtpConnection.message ?? t`SMTP connection successful!`,
      status: data.testSmtpConnection.success ? "success" : "error",
    });
  }

  @action.bound
  async sendTestEmail() {
    const input = await this.form.getCreateMutationInput(true);
    if (!input) return;

    const [data, error] = await this.sendTestEmailMutator.mutate({
      input,
    });

    if (error) return;

    this.appStore.UIStore.toast({
      title: data.sendTestEmail.message ?? t`Test email sent successfully!`,
      status: data.sendTestEmail.success ? "success" : "error",
    });
  }

  @computed
  get isTestButtonDisabled(): boolean {
    return this.form.isEmpty || this.form.form.hasError;
  }

  @computed
  get isOauthConnected(): boolean {
    return this.isGoogleConnected || this.isMicrosoftConnected;
  }
}
